// Sidebar.tsx
import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icons } from '@/icons';
import AddNewGamicsButton from '@/components/buttons/AddNewGamicsButton';
import { useSidebar } from '@/context/SidebarContext';
import AuthService from '@/services/AuthService';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import RemoteFile from '@/helper/RemoteFile';
import { ENABLE_CAMPAIGN } from '@/config/features';

const { Ticket, Signout } = Icons;

type SVGIcon = React.FC<React.SVGProps<SVGSVGElement>>;

interface NavItem {
  label: string;
  icon: SVGIcon;
  path: string;
}

const publisherNav: NavItem[] = [
  { label: 'Dashboard', icon: Icons.Chart, path: '/publisher/dashboard' },
  { label: 'Gamics', icon: Icons.Gamepad, path: '/publisher/gamics' },
  { label: 'AI Connect', icon: Icons.GroupShare, path: '/publisher/ai-connect' },
  ...(ENABLE_CAMPAIGN
    ? [{ label: 'Campaigns', icon: Icons.GroupShare, path: '/publisher/campaign' }]
    : []),
  { label: 'Billing', icon: Icons.Wallet, path: '/publisher/billing' },
  {
    label: 'Account Settings',
    icon: Icons.Setting,
    path: '/publisher/account-setting',
  },
  { label: 'Guide', icon: Icons.Wallet, path: '/publisher/guide' },
];

const adminNav: (NavItem & { permission?: string })[] = [
  { label: 'Dashboard', icon: Icons.Chart, path: '/admin/dashboard' }, // ✅ always visible
  {
    label: 'Users',
    icon: Icons.AdminUser,
    path: '/admin/users',
    permission: 'User',
  },
  {
    label: 'Gamics',
    icon: Icons.Gamepad,
    path: '/admin/gamics',
    permission: 'Gamic',
  },
  {
    label: 'Categories',
    icon: Icons.Category,
    path: '/admin/categories',
    permission: 'Category',
  },
  {
    label: 'Campaign',
    icon: Icons.GroupShare,
    path: '/admin/campaigns',
    permission: 'Campaign',
  },
  {
    label: 'Role & Permissions',
    icon: Icons.ChildAltLight,
    path: '/admin/roles',
    permission: 'Role-Permission',
  },
  {
    label: 'Account Settings',
    icon: Icons.Setting,
    path: '/admin/account-setting',
  },
  {
    label: 'Tickets',
    icon: Icons.Ticket,
    path: '/admin/tickets',
    permission: 'Ticket',
  },
  // QA #7: moderation queue. Gated by 'Gamic' permission since moderators
  // already have it. Hand-roll a tighter "Moderation" permission later.
  {
    label: 'Reports',
    icon: Icons.Ticket,
    path: '/admin/reports',
    permission: 'Gamic',
  },
];

const Sidebar: React.FC = () => {
  const { collapsed, toggle } = useSidebar();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const navigate = useNavigate();

  const isAdmin = pathname.startsWith('/admin');

  let navItems = isAdmin ? adminNav : publisherNav;

  if (isAdmin) {
    const roles = AuthService.getUserRoles();

    if (!roles.includes('admin')) {
      // ✅ moderator case → filter by permission
      navItems = adminNav.filter((item) => {
        if (!item.permission) return true; // items like Dashboard
        return AuthService.hasPermission(item.permission);
      });
    }
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleSignOut = async () => {
    const res = await AuthService.logout();
    if (res.success) navigate('/login', { replace: true });
  };

  const profile = AuthService.getUserProfile();
  const user = profile?.data?.[0];

  return (
    <aside
      className={[
        'bg-white shadow-md flex-shrink-0 transition-all duration-300',
        'flex flex-col min-h-screen',
        collapsed ? 'w-[80px]' : 'w-[300px]',
      ].join(' ')}
    >
      {/* logo */}
      <div className='flex flex-row p-6 h-[80px] items-center justify-between'>
        {collapsed ? (
          <Icons.HeaderManuSelected className='' onClick={toggle} />
        ) : (
          <>
            {' '}
            <img
              src='/assets/funizzle-logo-zip-file/OnlyLogo.svg'
              alt='Logo'
              className='w-8'
            />
            <Icons.HeaderManuUnSelected className='' onClick={toggle} />
          </>
        )}
      </div>

      {/* navigation */}
      <nav className='flex flex-col items-center'>
        {navItems.map(({ label, icon: Icon, path }) => (
          <NavLink key={path} to={path} className='w-full'>
            {({ isActive }) => (
              <div
                className={[
                  'flex h-[64px] hover:bg-[#FFE8EF]',
                  isActive ? 'bg-[#FFE8EF]' : '',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex items-center w-full px-6 py-4 lg:py-3 transition-colors h-full',
                    isActive
                      ? 'bg-[#FFE8EF] text-[#FF0050] font-medium'
                      : 'text-black hover:bg-[#FFE8EF] hover:text-[#FF0050] font-normal',
                  ].join(' ')}
                >
                  <Icon className='h-[32px] w-[32px] stroke-current' />
                  <span
                    className={[
                      'ml-3 text-[16px] font-normal',
                      collapsed ? 'hidden' : 'inline',
                    ].join(' ')}
                  >
                    {label}
                  </span>
                </div>
                <div
                  className={[
                    'w-[8px] self-stretch',
                    isActive
                      ? 'bg-[#FF0050] rounded-tl-2xl rounded-bl-2xl'
                      : '',
                  ].join(' ')}
                ></div>
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* extra buttons */}
      {!isAdmin && (
        <div className='mt-6 px-6 flex items-center justify-center'>
          <AddNewGamicsButton
            label='Add New Gamics'
            to='/publisher/add-gamics'
            responsive
            collapsed={collapsed}
          />
        </div>
      )}
      {isAdmin &&
        (() => {
          const roles = AuthService.getUserRoles();
          const isAdminRole = roles.includes('admin');
          const isModeratorRole = roles.includes('moderator');
          const canAddCampaign =
            isAdminRole ||
            (isModeratorRole && AuthService.hasPermission('Campaign'));

          if (!canAddCampaign) return null;

          return (
            <div className='mt-6 px-6 flex items-center justify-center'>
              <AddNewGamicsButton
                label='Add New Campaign'
                to='/admin/add-campaign'
                responsive
                collapsed={collapsed}
              />
            </div>
          );
        })()}

      <Box className='flex items-center mt-auto w-full px-4 lg:py-3'>
        <Box className='flex items-center border-t w-full pt-2'>
          <IconButton onClick={handleMenuOpen} size='small'>
            {user?.profile_pic ? (
              <RemoteFile
                keyPath={user.profile_pic}
                className='h-9 w-9 rounded-full'
              />
            ) : (
              <img
                src='/user.png'
                alt='User'
                className='h-8 w-8 rounded-full border-2 border-gray-300'
              />
            )}
          </IconButton>
          {collapsed ? '' :<span
            className={[
              'ml-2 text-[16px] font-normal',
              collapsed ? 'hidden lg:inline' : 'inline',
            ].join(' ')}
          >
            {/* {label} */}
            Profile
          </span>}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: -1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0px -2px 8px rgba(0,0,0,.5)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: 18,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              boxShadow: '0px 5px 2px rgba(0,0,0,.1)',
            },
          },
        }}
      >
        {!isAdmin && (
          <MenuItem
            component={NavLink}
            to='/publisher/support-tickets'
            onClick={handleMenuClose}
          >
            <ListItemIcon>
              <Ticket className='h-5 w-5  stroke-current text-black ' />
            </ListItemIcon>
            <ListItemText primary='Support Tickets' />
          </MenuItem>
        )}

        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <Signout className='h-5 w-5' />
          </ListItemIcon>
          <ListItemText primary='Sign Out' />
        </MenuItem>
      </Menu>
    </aside>
  );
};

export default Sidebar;
